import { useForm } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import BrowserData from "./browserData";

// Components
import ExpressCheckoutButtons from "@/components/Buttons/ExpressCheckoutButtons";
import PaymentMethod from "@/components/Forms/PaymentMethod";
import UserInformation from "@/components/Forms/UserInformation";
import Box from "@/components/Box";

// Store
import { useProductData } from "@/zustand/productStore";
import { useTemplateStore } from "@/zustand/templateStore";
import { useShapePaypalData } from "@/zustand/shapePaypalStore";

// Utils
import { createValidationSchema } from "@/func/validation";
import { usePaymentAvailability } from "@/hooks/usePaymentAvailability";
import { useEmailMarketingLead } from "@/hooks/useEmailMarketingLead";

// Types
import type { SubmitHandler } from "react-hook-form";
import type { AxiosRequestConfig } from "axios";
import type { ApplePayData, GooglePayData } from "@/types/types";

interface CheckoutFormValues {
  email?: string;
  first_name?: string;
  last_name?: string;
  address?: string;
  country?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  card_number?: string;
  card_exp_month?: string;
  card_exp_year?: string;
  card_cvc?: string;
  card_name?: string;
  expiration?: string;
}

// Props
interface FormProps {
  formSubmited: boolean;
  handleCreditCardSubmit: (
    data: CheckoutFormValues,
    options?: {
      setError?: (field: any, error: { type: string; message: string }) => void;
    },
  ) => void;
  handlePaypalSubmit: () => Promise<void>;
  paypalHandler: string;
  handleApplePaySubmit: (
    applePayData: ApplePayData,
    options?: AxiosRequestConfig,
  ) => Promise<void>;
  handleGooglePaySubmit: (
    googlePayData: GooglePayData,
    options?: AxiosRequestConfig,
  ) => Promise<void>;
}

export default function Form({
  formSubmited,
  handleCreditCardSubmit,
  handlePaypalSubmit,
  paypalHandler,
  handleApplePaySubmit,
  handleGooglePaySubmit,
}: FormProps) {
  const [country, setCountry] = useState<string | undefined>();
  const { country_code, allowed_payment_methods, paymentsConfig } =
    useProductData();
  const { form, paymentMethod } = useTemplateStore();
  const { isApplePayAvailable, isGooglePayAvailable } =
    usePaymentAvailability();
  const { applePayConfigured, googlePayConfigured, paypalEnabled } =
    paymentsConfig || {};
  const { setPaymentAvailability } = useTemplateStore();

  const activeApplePay =
    (allowed_payment_methods?.enable_applepay ?? false) &&
    isApplePayAvailable &&
    !!applePayConfigured;

  const activeGooglePay =
    (allowed_payment_methods?.enable_googlepay ?? false) &&
    isGooglePayAvailable &&
    !!googlePayConfigured;

  // Shape resolving an available account is an additional way for PayPal to
  // become active - it never takes away the static/local config's own
  // ability to enable it independently.
  const {
    available: shapePaypalAvailable,
    likely: shapePaypalLikely,
    resolved: shapePaypalResolved,
  } = useShapePaypalData();

  // Require a positive answer: `undefined` means the product pack API has not replied
  // yet, and treating that as enabled is what made the button flash in. Apple Pay,
  // Google Pay and Klarna already wait for their config the same way. The inlined
  // global toggle keeps a disabled site from ever considering it.
  //
  // `shapePaypalLikely` (while the real resolve is still in flight) lets the
  // button show immediately on a campaign that recently matched a Shape
  // routing rule, instead of waiting on that resolve call - see
  // shapePaypalStore.ts. It's dropped the moment the real resolve settles,
  // at which point `shapePaypalAvailable` alone decides.
  const activePaypal =
    (!!handlePaypalSubmit &&
      window.isPaypalEnabled !== false &&
      paypalEnabled === true) ||
    shapePaypalAvailable ||
    (shapePaypalLikely && !shapePaypalResolved);

  const validationSchema = createValidationSchema(form);
  const expirationRef = useRef<HTMLDivElement | null>(null);
  const itiRef = useRef<any>(null);

  const [initialValues, _setInitialValues] = useState<CheckoutFormValues>({
    email: "",
    first_name: "",
    last_name: "",
    address: "",
    country: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    card_number: "",
    card_exp_month: "",
    card_exp_year: "",
    card_cvc: "",
    card_name: "",
  });

  const {
    register,
    setValue,
    control,
    watch,
    getValues,
    handleSubmit,
    setError,
    formState: { errors, submitCount },
  } = useForm<CheckoutFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema) as any,
    context: {
      type: {
        method: paymentMethod,
        paypalHandler: paypalHandler,
      },
    },
    mode: "onTouched",
  });

  // Email Marketing Leads Logic
  useEmailMarketingLead();

  const submitForm: SubmitHandler<CheckoutFormValues> = (formData) => {
    handleCreditCardSubmit(formData, { setError });
  };

  useEffect(() => {
    setPaymentAvailability(isApplePayAvailable, isGooglePayAvailable);
  }, [isApplePayAvailable, isGooglePayAvailable, setPaymentAvailability]);

  useEffect(() => {
    setCountry(country_code);
    setValue("country", country_code ?? "");
  }, [country_code, setValue]);

  useEffect(() => {
    if (errors) {
      const errorValues = Object.values(errors);
      const types = errorValues.map((item) => item.type);
      const errorOnExpirationField = types.some(
        (type) => type === "test-exp_month" || type === "test-exp_year",
      );

      if (errorOnExpirationField) {
        expirationRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (errorValues.length > 0) {
        const firstErrorElement = document.getElementsByName(
          (errorValues[0] as any)?.ref?.name,
        )[0];
        firstErrorElement?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [errors]);

  useEffect(() => {
    const { country } = getValues();
    if (country) setCountry(country);
  }, [getValues]);

  const formNames = watch(["first_name", "last_name", "expiration"]);

  useEffect(() => {
    if (formNames[0]?.length && formNames[1]?.length) {
      setValue("card_name", `${formNames[0]} ${formNames[1]}`);
    }
    if (formNames[2]?.length && formNames[2]?.length > 3) {
      const [month, year] = formNames[2].split("/");
      month && setValue("card_exp_month", month);
      year && setValue("card_exp_year", `20${year}`);
    }
  }, [formNames, setValue]);

  return (
    <form
      className="flex flex-col gap-6"
      autoComplete="on"
      onSubmit={handleSubmit(submitForm)}
    >
      <BrowserData />
      <ExpressCheckoutButtons
        onApplePaySubmit={handleApplePaySubmit}
        onGooglePaySubmit={handleGooglePaySubmit}
        handlePaypalSubmit={handlePaypalSubmit}
        activeApplePay={activeApplePay}
        activeGooglePay={activeGooglePay}
        activePaypal={activePaypal}
      />
      <Box
        className={`shadow-cc gap-6 p-3 md:p-4 ${
          !form?.rounded_borders ? "rounded-none" : ""
        }`}
      >
        <UserInformation
          control={control}
          errors={errors}
          register={register}
          form={form}
          itiRef={itiRef}
          country={country}
          country_code={country_code}
          setCountry={setCountry}
        />
        <PaymentMethod
          paymentMethod={paymentMethod}
          formSubmited={formSubmited}
          submitCount={submitCount}
          setValue={setValue}
          register={register}
          errors={errors}
          control={control}
          expirationRef={expirationRef}
          form={form}
          values={getValues()}
          handlePaypalSubmit={handlePaypalSubmit}
          handleApplePaySubmit={handleApplePaySubmit}
          handleGooglePaySubmit={handleGooglePaySubmit}
          activeApplePay={activeApplePay}
          activeGooglePay={activeGooglePay}
          activePaypal={activePaypal}
        />
      </Box>
    </form>
  );
}
